/**
 * @fileoverview Data Retention Service for managing data retention types
 * Facade over DataRetentionSchedule.DataType namespace for React/TS codebase
 */
import { DataRetentionSchedule } from '@/js/external-js/variableModules/namespaces/DataRetentionSchedule.namespace';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

const DATA_RETENTION_API = '/v1/data-retention';

/** Singleton instance of the DataType namespace */
let DataTypeLib: typeof DataRetentionSchedule.DataType | null = null;

const initializeDataTypeLib = (): typeof DataRetentionSchedule.DataType => {
  if (!DataTypeLib) {
    DataTypeLib = DataRetentionSchedule.DataType;
  }
  return DataTypeLib;
};

export interface DataPool {
  id: string;
  name: string;
  reason?: string;
  dataTypes?: string[];  // IDs of assigned data types
  systems?: string[];    // IDs of assigned systems/assets
  suppliers?: string[];
  labels?: string[];
  status?: 'active' | 'inactive';
}

// --- Data Types ---

/**
 * Get all data retention types
 */
export const getDataTypes = async (): Promise<DataRetentionSchedule.DataTypeEntity[]> => {
  const lib = initializeDataTypeLib();
  const result = await lib.Get();
  if (Array.isArray(result)) return result;
  if (result && typeof result === 'object' && 'entity' in result) {
    return [(result as any).entity];
  }
  return [];
};

/**
 * Get a single data retention type by ID
 */
export const getDataTypeById = async (id: number): Promise<DataRetentionSchedule.DataTypeEntity | undefined> => {
  const lib = initializeDataTypeLib();
  const result = await lib.Get(id);
  if (result && typeof result === 'object' && 'entity' in result) {
    return (result as any).entity;
  } else {
    console.error('Failed to get data type by ID:', id, 'Result:', result);
  }
  return undefined;
};

/**
 * Create a new data retention type
 */
export const createDataType = async (id: number, data: Partial<DataRetentionSchedule.DataTypeEntity>) => {
  const lib = initializeDataTypeLib();
  return lib.Add(id, data);
};

/**
 * Update an existing data retention type
 */
export const updateDataType = async (id: number, updates: Partial<DataRetentionSchedule.DataTypeEntity>) => {
  const lib = initializeDataTypeLib();
  return lib.Edit(id, updates);
};

/**
 * Delete a data retention type
 */
export const deleteDataType = async (id: number) => {
  const lib = initializeDataTypeLib();
  return lib.Delete(id);
};

/**
 * Save multiple data types (used by suggestions modal)
 */
export const saveDataTypes = async (types: Partial<DataRetentionSchedule.DataTypeEntity>[]) => {
  const lib = initializeDataTypeLib();
  const promises = types.map(async (t) => {
      const id = t.dataTypeID || Math.floor(Math.random() * 1000000);
      // Check if exists? The API Add might upsert or fail. Assuming Add for new ones.
      // For this specific feature (onboarding suggestions), we are likely adding new ones.
      return lib.Add(id, t);
  });
  return Promise.all(promises);
};


// --- Data Pools ---

/**
 * Get all data pools via Thalassa API
 */
export const getDataPools = async (companyID: string): Promise<DataPool[]> => {
    if (!companyID) return [];
    try {
        const res = await deiterateAPI.GET(`${DATA_RETENTION_API}/pools`, {
            mode: 'cors',
            backend: 'thalassa',
            headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID }
        });
        return res.pools || [];
    } catch (error) {
        console.error('Error fetching data pools:', error);
        return [];
    }
};

/**
 * Save (Create/Update) a single data pool via Thalassa API
 */
export const saveDataPool = async (companyID: string, pool: DataPool) => {
    if (!companyID) throw new Error("Company ID required");
    await deiterateAPI.POST(`${DATA_RETENTION_API}/pools`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID },
        body: JSON.stringify(pool)
    });
};

/**
 * Delete a data pool via Thalassa API
 */
export const deleteDataPool = async (companyID: string, poolID: string) => {
    if (!companyID) throw new Error("Company ID required");
    await deiterateAPI.DELETE(`${DATA_RETENTION_API}/pools/${poolID}`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID }
    });
};

/**
 * Save multiple data pools (batch) via Thalassa API
 */
export const saveDataPools = async (companyID: string, pools: DataPool[]) => {
    if (!companyID) throw new Error("Company ID required");
    if (pools.length === 0) return;

    await deiterateAPI.POST(`${DATA_RETENTION_API}/pools/batch`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID },
        body: JSON.stringify({ pools })
    });
};

export default {
  getDataTypes,
  getDataTypeById,
  createDataType,
  updateDataType,
  deleteDataType,
  saveDataTypes,
  getDataPools,
  saveDataPool,
  deleteDataPool,
  saveDataPools
};
